/**
 * Page controls for the back-office lists.
 *
 * One component rather than the same three buttons written out on every
 * screen, because the thing that kept going wrong was not the buttons. It
 * was that a list served 25 rows with `has_more` in the response and the
 * screen rendered only the first page, so the rest was unreachable while the
 * heading cheerfully reported the full count.
 *
 * It also scrolls back to the top on every page change. These controls sit
 * at the bottom of a long list, so without it the next page arrives already
 * scrolled past — the reader lands at the end of page two and has to climb
 * back up to find where it starts.
 */
interface Props {
  page: number;
  /** The server's own flag where there is one; otherwise page*size < total. */
  hasMore: boolean;
  /** Matching the current filter, not the whole collection. */
  total?: number;
  /** What is being counted: "in the queue", "contributors", "codes". */
  noun?: string;
  /** Disables both directions while a page is in flight. */
  busy?: boolean;
  onChange: (page: number) => void;
  /**
   * Oldest-first lists read better as Newer/Older than as Previous/Next —
   * on the review queue, "next" is ambiguous about which way time runs.
   */
  labels?: { back: string; forward: string };
}

export function Pagination({
  page,
  hasMore,
  total,
  noun,
  busy = false,
  onChange,
  labels = { back: "← Previous", forward: "Next →" },
}: Props) {
  // A single page is not worth a control that cannot do anything.
  if (page <= 1 && !hasMore) return null;

  function go(next: number) {
    onChange(Math.max(1, next));
    // Instant, not smooth. A smooth scroll from the bottom of a long list
    // is still animating when the new rows render, and the height change
    // under it halts the animation part-way — measured landing 217px short
    // on the payout queue, which is the bug this is here to fix.
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  return (
    <nav className="row-between mt-24" aria-label="Pages">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={page <= 1 || busy}
        onClick={() => go(page - 1)}
      >
        {labels.back}
      </button>
      <span className="muted" style={{ fontSize: 12 }}>
        Page {page}
        {total === undefined ? "" : ` · ${total.toLocaleString()}${noun ? ` ${noun}` : ""}`}
      </span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={!hasMore || busy}
        onClick={() => go(page + 1)}
      >
        {labels.forward}
      </button>
    </nav>
  );
}
